import * as THREE from 'three';
import { ThreeJSManager } from '../core/ThreeJSManager';
import { InputManager } from '@/utils/InputManager';
import { PlayerController } from './player/PlayerController';
import { GameStateEnum } from '@/store/gameStateSlice';
import { store } from '@/store/store';
import { GameController } from './GameController';

/**
 * Main game manager that integrates all game components
 */
export class GameManager {
  private threeJSManager: ThreeJSManager;
  private inputManager: InputManager;
  private playerController: PlayerController | null = null;
  private gameController: GameController | null = null;
  private isInitialized: boolean = false;
  private gameState: GameStateEnum = GameStateEnum.MAIN_MENU;
  private isTransitioning: boolean = false;
  
  /**
   * Creates a new GameManager
   * @param canvas The HTML canvas element to render to
   */
  constructor(canvas: HTMLCanvasElement) {
    // Initialize Three.js
    this.threeJSManager = new ThreeJSManager(canvas);
    
    // Initialize Input Manager
    this.inputManager = new InputManager();
    
    // Subscribe to game state changes
    store.subscribe(() => {
      const state = store.getState();
      // Type assertion for state
      const gameState = (state as any).gameState?.currentState;
      if (gameState && gameState !== this.gameState) {
        this.onGameStateChanged(this.gameState, gameState);
      }
    });
    
    // Set initial game state
    const initialState = (store.getState() as any).gameState?.currentState;
    if (initialState) {
      this.gameState = initialState;
    }
  }
  
  /**
   * Initializes the game
   */
  public initialize(): void {
    if (this.isInitialized) return;
    
    console.log("Initializing game...");
    
    try {
      // Create player controller
      this.playerController = new PlayerController(
        this.threeJSManager.getScene(),
        this.threeJSManager.getCamera(),
        this.inputManager
      );
      
      // Set initial player position
      this.playerController.setPosition(new THREE.Vector3(0, 2, 10));
      
      // Create game controller
      this.gameController = new GameController(this);
      
      this.isInitialized = true;
      console.log("Game initialized successfully");
    } catch (error) {
      console.error("Error initializing game:", error);
    }
  }
  
  /**
   * Updates the game
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    // Only update gameplay elements if in WAVE_IN_PROGRESS state
    if (this.gameState === GameStateEnum.WAVE_IN_PROGRESS) {
      // Update player controller
      if (this.playerController) {
        this.playerController.update(deltaTime);
      }
      
      // Update game controller
      if (this.gameController) {
        this.gameController.update(deltaTime);
      }
    }
    
    // Always update Three.js
    this.threeJSManager.update();
  }
  
  /**
   * Handles game state changes
   * @param oldState The previous game state
   * @param newState The new game state
   */
  private onGameStateChanged(oldState: GameStateEnum, newState: GameStateEnum): void {
    // Prevent state changes while transitioning
    if (this.isTransitioning) {
      console.log("Ignoring state change during transition");
      return;
    }
    
    this.isTransitioning = true;
    
    // Only log if the state actually changed
    if (oldState !== newState) {
      console.log(`Game state changed from ${oldState} to ${newState}`);
    }
    
    this.gameState = newState;
    
    // Handle state transitions
    switch (newState) {
      case GameStateEnum.WAVE_IN_PROGRESS:
        // Initialize game if not already initialized
        if (!this.isInitialized) {
          this.initialize();
        }
        
        // Start game if coming from main menu
        if (oldState === GameStateEnum.MAIN_MENU) {
          if (this.gameController) {
            this.gameController.startGame();
          }
        }
        // Resume game if coming from paused
        else if (oldState === GameStateEnum.PAUSED) {
          if (this.gameController) {
            this.gameController.resumeGame();
          }
        }
        
        // Request pointer lock when entering gameplay
        if (oldState !== GameStateEnum.WAVE_IN_PROGRESS) {
          try {
            console.log("Requesting pointer lock...");
            this.inputManager.requestPointerLock();
          } catch (error) {
            console.error("Error requesting pointer lock:", error);
            // Don't revert to main menu on pointer lock failure
          }
        }
        break;
        
      case GameStateEnum.PAUSED:
        // Pause game
        if (this.gameController) {
          this.gameController.pauseGame();
        }
        
        // Release pointer lock when pausing
        if (this.inputManager.isPointerLock()) {
          this.inputManager.exitPointerLock();
        }
        break;
        
      case GameStateEnum.GAME_OVER:
      case GameStateEnum.VICTORY:
        // Release pointer lock
        if (this.inputManager.isPointerLock()) {
          this.inputManager.exitPointerLock();
        }
        break;
        
      default:
        break;
    }
    
    // Transition complete
    setTimeout(() => {
      this.isTransitioning = false;
    }, 100);
  }
  
  /**
   * Gets the Three.js manager
   * @returns The Three.js manager
   */
  public getThreeJSManager(): ThreeJSManager {
    return this.threeJSManager;
  }
  
  /**
   * Gets the input manager
   * @returns The input manager
   */
  public getInputManager(): InputManager {
    return this.inputManager;
  }
  
  /**
   * Gets the player controller
   * @returns The player controller
   */
  public getPlayerController(): PlayerController | null {
    return this.playerController;
  }
  
  /**
   * Gets the game controller
   * @returns The game controller
   */
  public getGameController(): GameController | null {
    return this.gameController;
  }
  
  /**
   * Cleans up resources when the game manager is no longer needed
   */
  public dispose(): void {
    if (this.playerController) {
      this.playerController.dispose();
    }
    
    if (this.gameController) {
      this.gameController.dispose();
    }
    
    this.threeJSManager.dispose();
    this.inputManager.dispose();
  }
} 